/**
 * An email template's words as they are stored, drawn and sent (piece 5; decision 0965).
 *
 * 🔒 The email consumers' allowed table, layered over the shared one the way the
 * article's is (decision 0800): what the email toolbar writes — a font face, a
 * size, a text colour, a highlight and a picture — survives the save, and
 * nothing else does. 🔒 A picture is a File: the words keep its id and a line of
 * words, never an address, and a send carries its bytes as an inline attachment
 * the body names by `cid:`.
 */
import { RICH_TEXT_ALIGNMENTS, RICH_TEXT_TAGS, richTextStartOf, sanitizeRichText, type RichTextAllowance } from '../controls/richText';
import { articleColourOf } from '../knowledge/body';
import { ARTICLE_PICTURE_MAX_BYTES, ARTICLE_PICTURE_MIME_TYPES, articlePictureProblem } from '../knowledge/pictures';
import { FONT_FAMILIES, FONT_SIZES, LINE_SPACINGS, fontPointSizeOf, fontPointValue } from './richText';

/** The seeded FileType every email template picture takes; the picture tool never asks for one. */
export const EMAIL_PICTURE_FILE_TYPE_NAME = 'Email Picture';

/** The same four rasters and five megabytes the article's picture tool takes: what every mail program draws. */
export const EMAIL_PICTURE_MIME_TYPES = ARTICLE_PICTURE_MIME_TYPES;
export const EMAIL_PICTURE_MAX_BYTES = ARTICLE_PICTURE_MAX_BYTES;
export const emailPictureProblem = articlePictureProblem;

/** Where the Template Manager's picture tool uploads, relative to the API root (piece 1). */
export const EMAIL_TEMPLATE_PICTURES_PATH = '/email-templates/pictures';

/**
 * 🔒 The email consumers' allowed table (decision 0965): the shared tags, the
 * `font` element the font, size and colour tools write, a styled span for the
 * highlight, and a picture by its File's id. 🔒 A table joins them with the
 * email toolbar's Insert a table (decision 1537), bare, as that tool writes it.
 * 🔒 The `font` element's `size` is kept for a body stored before decision 1615
 * and never written; the Size menu writes a span's `font-size` in points.
 */
export const EMAIL_RICH_TEXT_TAGS: Readonly<Record<string, readonly string[]>> = {
  ...RICH_TEXT_TAGS,
  font: ['face', 'size', 'color'],
  span: ['style'],
  img: ['data-mail-file', 'alt'],
  table: [], thead: [], tbody: [], tr: [], th: [], td: [],
};

/**
 * 🔒 What writes each element, attribute and declaration the email table keeps
 * beyond the shared one: a tool of the email toolbar. The toolbar test reads it
 * both ways, so a kept element nothing draws and a drawn tool whose output is
 * dropped are each a failure.
 */
export const EMAIL_ELEMENT_WRITERS: Readonly<Record<string, string>> = {
  'font[face]': 'fontFamily', 'font[size]': 'fontSize', 'font-size': 'fontSize', 'font[color]': 'textColor', 'background-color': 'highlightColor', color: 'textColor', img: 'image', 'text-align': 'alignCenter',
  'line-height': 'lineSpacing', table: 'table', thead: 'table', tbody: 'table', tr: 'table', th: 'table', td: 'table',
};

/** The declarations a kept style may carry, in the one order they are written back; the line spacing joined them with decision 1537, the size with 1615. */
export const EMAIL_STYLE_PROPERTIES = ['text-align', 'font-size', 'color', 'background-color', 'line-height'] as const;
type EmailStyleProperty = (typeof EMAIL_STYLE_PROPERTIES)[number];

/**
 * 🔒 A style read as an email keeps it (decision 0965): the alignment, the size
 * in points (decision 1615), the text colour, the highlight and the line spacing
 * (decision 1537), each checked and written back one way. 🔨 Anything
 * else a style carries is dropped; a style is never kept as the text it arrived as.
 */
export function emailStyleOf(style: string): string | null {
  const found = new Map<EmailStyleProperty, string>();
  for (const declaration of style.split(';')) {
    const colon = declaration.indexOf(':');
    if (colon < 0) continue;
    const property = declaration.slice(0, colon).trim().toLowerCase() as EmailStyleProperty;
    const value = declaration.slice(colon + 1).trim().toLowerCase();
    if (found.has(property)) continue;
    if (property === 'text-align' && (RICH_TEXT_ALIGNMENTS as readonly string[]).includes(value)) found.set(property, value);
    // 🔒 A size of the ladder in points, and no other (decision 1615).
    if (property === 'font-size') {
      const points = fontPointSizeOf(value);
      if (points) found.set(property, fontPointValue(points));
    }
    if (property === 'color' || property === 'background-color') {
      const colour = articleColourOf(value);
      if (colour) found.set(property, colour);
    }
    // 🔒 One of the four line spacings the toolbar writes, and no other (decision 1537).
    if (property === 'line-height' && (LINE_SPACINGS as readonly string[]).includes(value)) found.set(property, value);
  }
  const kept = EMAIL_STYLE_PROPERTIES.filter((property) => found.has(property)).map((property) => `${property}: ${found.get(property)};`);
  return kept.length ? kept.join(' ') : null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A font face as one of the five the menu offers, written as the menu writes it, or null for anything else. */
function faceOf(raw: string): string | null {
  const bare = raw.replace(/&quot;|&#39;|["']/g, '').trim().toLowerCase();
  return FONT_FAMILIES.find((family) => family.toLowerCase() === bare) ?? null;
}

/** The email consumers' allowance: their table, their style reader, and each attribute's value checked. */
export const EMAIL_ALLOWANCE: RichTextAllowance = {
  tags: EMAIL_RICH_TEXT_TAGS,
  style: emailStyleOf,
  attribute: (name, value) => {
    if (name === 'face') return faceOf(value);
    if (name === 'size') return (FONT_SIZES as readonly string[]).includes(value.trim()) ? value.trim() : null;
    if (name === 'color') return articleColourOf(value);
    if (name === 'start') return richTextStartOf(value);
    if (name === 'data-mail-file') return UUID.test(value.trim()) ? value.trim().toLowerCase() : null;
    if (name === 'alt') return value.replace(/\s+/g, ' ').trim().slice(0, 200);
    return value.trim();
  },
};

const PICTURE = /<img\b([^>]*)>/gi;
const PICTURE_ID = /\bdata-mail-file="([0-9a-f-]{36})"/i;
const PICTURE_ALT = /\balt="([^"]*)"/i;

/** 🔨 A picture in its one stored form, its File's id then its words, or nothing at all where it names no File. */
function canonicalPictures(safe: string): string {
  return safe.replace(PICTURE, (_whole, attributes: string) => {
    const id = PICTURE_ID.exec(attributes)?.[1];
    if (!id) return '';
    const alt = PICTURE_ALT.exec(attributes)?.[1];
    return `<img data-mail-file="${id.toLowerCase()}"${alt ? ` alt="${alt}"` : ''}>`;
  });
}

/** 🔒 An email template's body made safe to store: the email table, and every picture in its one stored form (decision 0965). */
export function sanitizeEmailBody(html: string): string {
  return canonicalPictures(sanitizeRichText(html, EMAIL_ALLOWANCE));
}

/** Every File the words draw as a picture, once each, in the order they first appear. */
export function emailPictureIds(html: string): string[] {
  const ids: string[] = [];
  for (const match of html.matchAll(new RegExp(PICTURE.source, 'gi'))) {
    const id = PICTURE_ID.exec(match[1] ?? '')?.[1]?.toLowerCase();
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/**
 * Each picture given the address it is drawn from — its bytes inline for the
 * Template Manager, its `cid:` in a sent message — keeping its id so a save
 * reads it back. A picture with no address to give is left out rather than
 * drawn broken.
 */
export function emailPicturesResolved(html: string, src: (fileId: string) => string | null): string {
  return html.replace(PICTURE, (_whole, attributes: string) => {
    const id = PICTURE_ID.exec(attributes)?.[1]?.toLowerCase();
    const address = id ? src(id) : null;
    if (!id || !address) return '';
    const alt = PICTURE_ALT.exec(attributes)?.[1];
    return `<img src="${address.replace(/"/g, '&quot;')}" data-mail-file="${id}"${alt ? ` alt="${alt}"` : ''}>`;
  });
}

const DRAWN_SRC = /\bsrc="(data:image\/(?:png|jpeg|gif|webp);base64,[A-Za-z0-9+/=]+)"/i;

/**
 * 🔒 A template body as a screen draws it (decision 0965): the email table on
 * every render, and each picture given back the bytes the server drew it with.
 * 🔨 Only a picture's own inline bytes survive, never an address it could be
 * fetched from; a picture that arrived without them is left out.
 */
export function sanitizeEmailView(html: string): string {
  const drawn = new Map<string, string>();
  for (const match of html.matchAll(new RegExp(PICTURE.source, 'gi'))) {
    const attributes = match[1] ?? '';
    const id = PICTURE_ID.exec(attributes)?.[1]?.toLowerCase();
    const src = DRAWN_SRC.exec(attributes)?.[1];
    if (id && src && !drawn.has(id)) drawn.set(id, src);
  }
  return emailPicturesResolved(sanitizeEmailBody(html), (id) => drawn.get(id) ?? null);
}

/** The content id a sent message names a picture by: its File's id at a domain of the application's own. */
export function emailPictureContentId(fileId: string): string {
  return `${fileId.toLowerCase()}@picture.bms2`;
}
